import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Box,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tab,
    TabList,
    Tabs,
} from "@chakra-ui/react";
import {
    walletDialogPaperSx,
    walletCancelButtonSx,
} from "../artistWallet/dialogStyles";
import CreateFreeForm, {
    buildFreeInitialState,
    buildFreePayload,
    validateFreeForm,
} from "./CreateFreeForm";
import CreatePaidForm, {
    buildPaidInitialState,
    buildPaidPayload,
    validatePaidForm,
} from "./CreatePaidForm";
import PaidSuccessPanel from "./PaidSuccessPanel";

const MODES = ["free", "paid"];

const primaryButtonProps = {
    background: "rgba(23, 220, 184, 0.15)",
    color: "#17dcb8",
    border: "1px solid rgba(23, 220, 184, 0.4)",
    fontWeight: 600,
    _hover: { background: "rgba(23, 220, 184, 0.25)" },
    _disabled: { opacity: 0.5 },
};

const tabProps = {
    color: "rgba(255, 255, 255, 0.6)",
    fontWeight: 600,
    _selected: { color: "#17dcb8", borderColor: "#17dcb8" },
};

/**
 * Posts the free creation payload and returns true on success.
 * @param {Object} args - Submission arguments
 * @returns {Promise<boolean>}
 */
async function submitFree({ payload, setLoading, onCreated, onClose }) {
    setLoading(true);
    try {
        const { data } = await axios.post("/admin/subscriptions/create-free", payload);
        toast.success("Abonnement gratuit créé");
        onCreated?.(data);
        onClose();
        return true;
    } catch (error) {
        const msg = error?.response?.data || "Échec de la création";
        toast.error(typeof msg === "string" ? msg : "Échec de la création");
        return false;
    } finally {
        setLoading(false);
    }
}

/**
 * Posts the paid creation payload and stores the result so the success panel
 * can display the setup_link. Returns the API response or null on failure.
 * @param {Object} args - Submission arguments
 * @returns {Promise<Object|null>}
 */
async function submitPaid({ payload, setLoading, setPaidResult, onCreated }) {
    setLoading(true);
    try {
        const { data } = await axios.post("/admin/subscriptions/create-paid", payload);
        toast.success("Abonnement créé — lien envoyé");
        setPaidResult(data);
        onCreated?.(data);
        return data;
    } catch (error) {
        const msg = error?.response?.data || "Échec de la création";
        toast.error(typeof msg === "string" ? msg : "Échec de la création");
        return null;
    } finally {
        setLoading(false);
    }
}

/**
 * Resets all dialog state on open. Centralizes side-effect to keep the parent tidy.
 * @param {Object} args - Reset arguments
 */
function useResetOnOpen({ open, setMode, setFreeForm, setPaidForm, setPaidResult }) {
    useEffect(() => {
        if (!open) return;
        setMode("free");
        setFreeForm(buildFreeInitialState());
        setPaidForm(buildPaidInitialState());
        setPaidResult(null);
    }, [open, setMode, setFreeForm, setPaidForm, setPaidResult]);
}

/**
 * Tab switcher between Gratuit and Payant modes.
 * @param {Object} props
 * @param {string} props.mode - Current mode ("free" | "paid")
 * @param {Function} props.onChange - Mode change handler
 * @returns {React.ReactElement}
 */
function ModeTabs({ mode, onChange }) {
    return (
        <Tabs index={MODES.indexOf(mode)} onChange={(i) => onChange(MODES[i])}
            variant="line" isFitted mb={4}>
            <TabList borderColor="rgba(255, 255, 255, 0.12)">
                <Tab {...tabProps}>Gratuit</Tab>
                <Tab {...tabProps}>Payant</Tab>
            </TabList>
        </Tabs>
    );
}

/**
 * Renders the dialog body: form, success panel, or active mode form.
 * @param {Object} props - Dialog body props
 * @returns {React.ReactElement}
 */
function DialogBody({ mode, setMode, freeForm, setFreeForm, paidForm, setPaidForm, paidResult }) {
    if (paidResult) return <PaidSuccessPanel result={paidResult} />;
    return (
        <Box>
            <ModeTabs mode={mode} onChange={setMode} />
            {mode === "free"
                ? <CreateFreeForm form={freeForm} setForm={setFreeForm} />
                : <CreatePaidForm form={paidForm} setForm={setPaidForm} />}
        </Box>
    );
}

/**
 * Builds the action button row according to dialog state (form vs paid success).
 * @param {Object} props - Action button props
 * @returns {React.ReactElement}
 */
function DialogActionsRow({ paidResult, loading, onClose, onSubmit }) {
    if (paidResult) {
        return (
            <ModalFooter>
                <Button onClick={onClose} {...primaryButtonProps}>Fermer</Button>
            </ModalFooter>
        );
    }
    return (
        <ModalFooter gap={2}>
            <Button onClick={onClose} {...walletCancelButtonSx}>Annuler</Button>
            <Button onClick={onSubmit} isDisabled={loading} {...primaryButtonProps}>
                {loading ? "Création..." : "Créer"}
            </Button>
        </ModalFooter>
    );
}

/**
 * Validates the active form, builds the payload, and dispatches to the right endpoint.
 * @param {Object} args - Submit arguments (mode, forms, callbacks)
 * @returns {Promise<void>}
 */
async function handleSubmit({ mode, freeForm, paidForm, userId, setLoading, setPaidResult, onCreated, onClose }) {
    if (mode === "free") {
        const err = validateFreeForm(freeForm);
        if (err) { toast.error(err); return; }
        await submitFree({ payload: buildFreePayload(freeForm, userId), setLoading, onCreated, onClose });
        return;
    }
    const err = validatePaidForm(paidForm);
    if (err) { toast.error(err); return; }
    await submitPaid({ payload: buildPaidPayload(paidForm, userId), setLoading, setPaidResult, onCreated });
}

/**
 * Modal to create a new subscription (free or paid) for a given user.
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close callback
 * @param {number} props.userId - Target user ID
 * @param {Function} props.onCreated - Called after a successful creation (parent refresh)
 * @returns {React.ReactElement}
 */
function CreateSubscriptionDialog({ open, onClose, userId, onCreated }) {
    const [mode, setMode] = useState("free");
    const [freeForm, setFreeForm] = useState(buildFreeInitialState());
    const [paidForm, setPaidForm] = useState(buildPaidInitialState());
    const [paidResult, setPaidResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useResetOnOpen({ open, setMode, setFreeForm, setPaidForm, setPaidResult });

    const onSubmit = () => handleSubmit({
        mode, freeForm, paidForm, userId, setLoading, setPaidResult, onCreated, onClose,
    });

    return (
        <Modal isOpen={open} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent {...walletDialogPaperSx}>
                <ModalHeader color="rgba(255, 255, 255, 0.95)" fontWeight={600}>
                    Nouvel abonnement
                </ModalHeader>
                <ModalBody>
                    <DialogBody mode={mode} setMode={setMode}
                        freeForm={freeForm} setFreeForm={setFreeForm}
                        paidForm={paidForm} setPaidForm={setPaidForm}
                        paidResult={paidResult} />
                </ModalBody>
                <DialogActionsRow paidResult={paidResult} loading={loading} onClose={onClose} onSubmit={onSubmit} />
            </ModalContent>
        </Modal>
    );
}

export default CreateSubscriptionDialog;
